import { useEffect, useState, useRef } from "react";
import {
  Table,
  ScrollArea,
  Card,
  Button,
  LoadingOverlay,
  createStyles,
  Group,
  Paper,
  SimpleGrid,
  Text,
  Stack,
  Divider,
  Image,
  Badge,
} from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import axios from "axios";
import { API } from "utiles/url";
import { customLoader } from "components/utilities/loader";
import { Download } from "tabler-icons-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const useStyles = createStyles((theme) => ({
  sectionTitle: {
    fontWeight: 700,
    fontSize: theme.fontSizes.sm,
    textTransform: "uppercase",
    color: theme.colors.blue[7],
    marginBottom: theme.spacing.xs,
  },
  label: {
    color: theme.colors.gray[6],
    fontWeight: 500,
    fontSize: theme.fontSizes.xs,
  },
  value: { fontWeight: 600, fontSize: theme.fontSizes.sm },
  card: {
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
  },
}));

function ProductDetailModal({ Id }) {
  const { classes } = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    if (Id) fetchDriverData();
  }, [Id]);

  const fetchDriverData = async () => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await axios.get(`${API}/drivers/${Id}`, config);
      setData(data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    const element = reportRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Driver_Report_${Id}.pdf`);
  };

  const renderVal = (val) => {
    if (val === null || val === undefined) return "N/A";
    if (typeof val === "object") return val.name || "N/A";
    return String(val);
  };

  const InfoRow = ({ label, value }) => (
    <Group position="apart" noWrap>
      <Text className={classes.label}>{label}:</Text>
      <Text className={classes.value}>{renderVal(value)}</Text>
    </Group>
  );

  const getMediaUrl = (collectionName) => {
    return data?.vehicle?.media?.find(
      (m) => m.collection_name === collectionName,
    )?.original_url;
  };

  const { height } = useViewportSize();
  return (
    <ScrollArea style={{ height: height / 1.2 }} type="auto" offsetScrollbars>
      {data && (
        <div style={{ width: "96%", margin: "auto", paddingBottom: 40 }}>
          <LoadingOverlay
            visible={loading}
            overlayBlur={2}
            loader={customLoader}
          />

          <Group position="apart" mb="md" mt="md">
            <Badge
              size="lg"
              color={data?.is_on_duty ? "green" : "red"}
              variant="filled"
            >
              {data?.is_on_duty ? "On Duty" : "Off Duty"}
            </Badge>
            <Button
              variant="outline"
              color="blue"
              leftIcon={<Download size={16} />}
              onClick={exportToPDF}
            >
              Export Driver & Vehicle Only
            </Button>
          </Group>

          <div
            ref={reportRef}
            style={{
              padding: "20px",
              backgroundColor: "#fff",
              borderRadius: "8px",
            }}
          >
            <SimpleGrid
              cols={2}
              breakpoints={[{ maxWidth: "sm", cols: 1 }]}
              mb="xl"
            >
              <Paper withBorder p="lg" radius="md" className={classes.card}>
                <Text className={classes.sectionTitle}>Driver Detail</Text>
                <Divider mb="sm" />
                <Stack spacing="xs">
                  <InfoRow
                    label="Name"
                    value={data?.user?.name || `User ID: ${data?.user_id}`}
                  />
                  <InfoRow label="Phone" value={data?.user?.phone} />

                  <InfoRow label="City" value={data?.city} />
                  <InfoRow label="Region" value={data?.region} />
                  <InfoRow
                    label="License Number"
                    value={data?.vehicle?.license_number}
                  />
                </Stack>
              </Paper>

              <Paper withBorder p="lg" radius="md" className={classes.card}>
                <Text className={classes.sectionTitle}>Vehicle Detail</Text>
                <Divider mb="sm" />
                <Stack spacing="xs">
                  <InfoRow label="Model" value={data?.vehicle?.model} />
                  <InfoRow
                    label="Plate Number"
                    value={data?.vehicle?.plate_number}
                  />
                  <InfoRow label="Color" value={data?.vehicle?.color} />
                  <InfoRow
                    label="Owner Name"
                    value={data?.vehicle?.owner_name}
                  />
                  <InfoRow
                    label="Vehicle Type"
                    value={data?.vehicle?.vehicle_type?.title}
                  />
                      <InfoRow
                    label="Libre Number"
                    value={data?.vehicle?.libre_number}
                  />
                </Stack>
              </Paper>
            </SimpleGrid>
          </div>

          <Card withBorder radius="md" p="md" mt="xl">
            <Text weight={700} size="sm" mb="md">
              RECENT DROPOFFS (Not included in Export)
            </Text>
            <Table verticalSpacing="sm">
              <thead>
                <tr>
                  <th>From</th>
                  <th>Status</th>
                  <th>Cost</th>
                  <th>All Received</th>
                </tr>
              </thead>
              <tbody>
                {data?.dropoffs?.map((item) => (
                  <tr key={item.id}>
                    <td>{item.from?.name}</td>
                    <td>
                      <Badge
                        color={item.status === "FINISHED" ? "green" : "blue"}
                      >
                        {item.status}
                      </Badge>
                    </td>
                    <td>{item.cost}</td>
                    <td>{item.all_received ? "✅" : "❌"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Divider
            my="xl"
            label="Verification Documents (Not included in Export)"
            labelPosition="center"
          />

          <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
            <Stack>
              <Text weight={600} size="sm" color="dimmed">
                Driver License
              </Text>
              <Paper
                withBorder
                radius="md"
                p="xs"
                sx={{ height: 250, overflow: "hidden" }}
              >
                <Image
                  src={getMediaUrl("license_doc")}
                  alt="License"
                  height={230}
                  fit="contain"
                  withPlaceholder
                />
              </Paper>
            </Stack>

            <Stack>
              <Text weight={600} size="sm" color="dimmed">
                Vehicle Libre
              </Text>
              <Paper
                withBorder
                radius="md"
                p="xs"
                sx={{ height: 250, overflow: "hidden" }}
              >
                <Image
                  src={getMediaUrl("libre_doc")}
                  alt="Libre"
                  height={230}
                  fit="contain"
                  withPlaceholder
                />
              </Paper>
            </Stack>
          </SimpleGrid>
        </div>
      )}
    </ScrollArea>
  );
}

export default ProductDetailModal;
